import { AlertCircleIcon, BugIcon } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { roles } from '@/modules/auth/roles';
import { hasAnyRole } from '@/modules/auth/utils/hasRoles';
import { useCoordinationProcessesQuery } from '@/modules/coordination/queries/useCoordinationProcessesQuery';
import { JobInboundSettingsSection } from '@/modules/jobs/components/inboundSettings/JobInboundSettingsSection';
import { JobNotesList } from '@/modules/jobs/components/jobNotes/JobNotesList';
import { SubmissionDecisionModeSettings } from '@/modules/jobs/components/SubmissionDecisionModeSettings';
import { useSubmissionDecisionModeMutation } from '@/modules/jobs/mutations/useSubmissionDecisionModeMutation';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import { JobSignalPanel } from '@/modules/jobs/signal/components/JobSignalPanel';
import {
  jobToRoleDraft,
  jobToWorkflowDraft,
} from '@/modules/jobs/signal/jobRequestMappers';
import { useJobEditSubmit } from '@/modules/jobs/signal/useJobEditSubmit';
import type { SubmissionDecisionModeApi } from '@/modules/jobs/types';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { JobAiDisclosureSection } from '@/modules/legal/components/job-ai-disclosure/JobAiDisclosureSection';
import { PublicShortlistSettings } from '@/modules/public-shortlist/components/PublicShortlistSettings';
import { PUBLIC_SHORTLIST_SETTINGS_ID } from '@/modules/public-shortlist/constants';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import Container from '@/modules/ui/components/container/Container';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

export const JobEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const currentUserQuery = useCurrentUserQuery();
  const isSuperAdmin = hasAnyRole(
    [roles.SuperAdmin],
    currentUserQuery.data?.roles ?? [],
  );

  const jobQuery = useJobQuery(id!);
  const coordinationProcessesQuery = useCoordinationProcessesQuery(id);
  const decisionModeMutation = useSubmissionDecisionModeMutation();
  const editSubmit = useJobEditSubmit(jobQuery.data, () => navigate('/jobs'));

  useEffect(() => {
    if (window.location.hash !== `#${PUBLIC_SHORTLIST_SETTINGS_ID}`) {
      return;
    }

    document
      .getElementById(PUBLIC_SHORTLIST_SETTINGS_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [jobQuery.data]);

  const pageTitle = jobQuery.data?.title ? jobQuery.data.title : 'Edit Job';

  const handleDecisionModeChange = (mode: SubmissionDecisionModeApi) => {
    if (!id) return;

    decisionModeMutation.mutate(
      { jobId: id, mode },
      {
        onSuccess: () => {
          toast.success(`Submission decision mode changed to ${mode}`);
        },
        onError: (error) => {
          toast.error(
            `Submission decision mode has not been changed: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <>
      <DocumentMetadata title={pageTitle} />

      <Container>
        <PageHeader
          title={pageTitle}
          subtitle={
            jobQuery.data?.company && (
              <Link
                to={`/companies/${jobQuery.data.company.id}/edit`}
                className="hover:underline"
              >
                {jobQuery.data.company.name}
              </Link>
            )
          }
          action={
            <div className="flex items-center gap-2">
              {isSuperAdmin && id && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/debug/job-sourcing/${id}`}>
                    <BugIcon className="size-4" />
                    Debug
                  </Link>
                </Button>
              )}
              <Button asChild>
                <Link to={`/jobs/${id}/candidates`}>View Candidates</Link>
              </Button>
            </div>
          }
        />

        {jobQuery.isLoading && <Loader />}

        {jobQuery.isError && (
          <Alert variant="destructive">
            <AlertCircleIcon />
            <AlertDescription>Job not found</AlertDescription>
          </Alert>
        )}

        {jobQuery.data && (
          <div className="space-y-8 pb-8">
            {/* Panel is the full recruiter edit surface: the settings card carries the lifecycle &
                outreach controls. */}
            <JobSignalPanel
              // key by job id: the panel seeds its drafts once on mount — without the key, navigating
              // A-edit → B-edit → A-edit (cached data, no unmount) would save B's drafts into A
              key={jobQuery.data.id}
              mode="edit"
              initialRole={jobToRoleDraft(jobQuery.data)}
              initialWorkflow={jobToWorkflowDraft(jobQuery.data)}
              canPublish={jobQuery.data.sourcingState === 'Draft'}
              hasCoordinationProcess={
                (coordinationProcessesQuery.data?.length ?? 0) > 0
              }
              legacySourcingStrategy={
                jobQuery.data.sourcingStrategy !== 'Agent'
                  ? jobQuery.data.sourcingStrategy
                  : undefined
              }
              submitting={editSubmit.submitting}
              submitError={editSubmit.error}
              onSubmit={editSubmit.onSubmit}
            />

            <div className="mx-auto max-w-6xl space-y-8 px-6">
              <SubmissionDecisionModeSettings
                mode={jobQuery.data.submissionDecisionMode}
                isPending={decisionModeMutation.isPending}
                onChange={handleDecisionModeChange}
              />

              <PublicShortlistSettings job={jobQuery.data} />

              <PermissionsGate permissions={[permissions.Inbound.Manage]}>
                <JobInboundSettingsSection jobId={jobQuery.data.id} />
              </PermissionsGate>

              <PermissionsGate
                permissions={[permissions.Jobs.UpdateAiDisclosure]}
              >
                <JobAiDisclosureSection
                  key={jobQuery.data.id}
                  job={jobQuery.data}
                />
              </PermissionsGate>

              {id && <JobNotesList jobId={id} />}
            </div>
          </div>
        )}
      </Container>
    </>
  );
};
